import React, { useCallback, useEffect, useMemo, useState } from "react";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import {
  EntityType,
  entityTypeToEntityName,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import {
  useEntityFieldsWithAdditionals,
  useFieldsSections,
  useUpdateEntityField,
  FieldTypeLevel
} from "@/pages/settings/hooks";
import { APIFieldItem, FieldSection } from "@/pages/settings/types";
import DetailFields, {
  DetailFieldsContext,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import { createFieldsConfigFromAPI } from "@/utils/entityFieldsUtils/detailFields";
import {
  FieldSectionPage,
  getPageFieldSections
} from "@/utils/entityFieldsUtils/fieldSections";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import { useThresholds } from "@/entities/settings/hooks";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { get, groupBy, sortBy } from "lodash";
import { getParsedFieldValue } from "@/utils/entityFieldsUtils/shared";
import InlineLoader from "@/components/ui/inline-loader";

interface Props {
  entityData: EntityData;
  entityType: EntityType;
  onUpdateField: (
    field: string,
    newValue: string | number,
    additionalFields?: {
      [fieldName: string]: string | number;
    },
    successToastText?: string,
    onSuccessFn?: () => void
  ) => void;
  refetchEntityWithAdditionals?: () => void;
}

interface SectionWithFields {
  section: FieldSection;
  fields: APIFieldItem[];
  fieldsConfig: FieldsConfig;
}

const AdditionalInfoTab: React.FC<Props> = ({
  entityData,
  entityType,
  onUpdateField,
  refetchEntityWithAdditionals
}) => {
  const { t } = useTranslation();
  const { thresholds } = useThresholds();

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);

  const { data: fieldsResponse, isLoading: isLoadingFields } =
    useEntityFieldsWithAdditionals(entityType, {
      fieldTypeLevel: FieldTypeLevel.ADDITIONAL
    });
  const { data: fieldsSectionsResponse, isLoading: isLoadingSections } =
    useFieldsSections(entityType);
  // Destructured: useMutation returns a fresh object every render, so keeping
  // the whole thing as a dependency below left this callback — and the context
  // and field configs built from it — rebuilding every render anyway.
  const { mutateAsync: updateEntityFieldAsync } =
    useUpdateEntityField(entityType);

  const entityId = entityData?.id || "";
  const useEntityQuery = getEntityHook(entityType);
  const entityQuery = useEntityQuery
    ? useEntityQuery([entityId], {
        forceRefetch: true,
        include_additionals: true
      })
    : { entities: [], isSuccess: false };

  const { entities: [fetchedEntityWithAdditionals] = [] } = entityQuery;

  const [pendingUpdates, setPendingUpdates] = useState<
    Record<string, string | number>
  >({});

  const baseEntityData = fetchedEntityWithAdditionals || entityData;
  const currentEntityData = useMemo(
    () =>
      baseEntityData
        ? { ...baseEntityData, ...pendingUpdates }
        : baseEntityData,
    [baseEntityData, pendingUpdates]
  );

  const isLoading = isLoadingFields || isLoadingSections;

  useEffect(() => {
    if (refetchEntityWithAdditionals) {
      refetchEntityWithAdditionals();
    }
  }, [refetchEntityWithAdditionals]);

  const onUpdateFieldWithAdditionals = useCallback(
    (
      field: string,
      newValue: string | number,
      additionalFields?: { [fieldName: string]: string | number },
      successToastText?: string,
      onSuccessFn?: () => void
    ) => {
      setPendingUpdates((prev) => ({
        ...prev,
        [field]: newValue,
        ...(additionalFields || {})
      }));

      (onUpdateField as any)(
        field,
        newValue,
        additionalFields,
        successToastText,
        onSuccessFn,
        true,
        undefined,
        { include_additionals: "true" }
      );
    },
    [onUpdateField]
  );

  const handleCreateNewOption = useCallback(async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = fieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      const entityTypeName = entityTypeToEntityName[entityType];

      await updateEntityFieldAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: entityTypeName,
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      // Auto-select the newly created option
      if (onUpdateFieldWithAdditionals) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          const fieldConfig = fieldsResponse?.fields?.find(
            (f) => f.id === currentFieldForNewOption.fieldId
          )?.config;
          const rawValue = get(
            currentEntityData,
            currentFieldForNewOption.fieldKey
          );

          const currentValues: string[] = getParsedFieldValue(rawValue, {
            type: "multiSelect",
            ...fieldConfig
          });
          const newValues = Array.isArray(currentValues)
            ? [...currentValues, optionName]
            : [optionName];
          onUpdateFieldWithAdditionals(
            currentFieldForNewOption.fieldKey,
            newValues as any
          );
        } else {
          onUpdateFieldWithAdditionals(
            currentFieldForNewOption.fieldKey,
            optionName
          );
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  }, [
    currentFieldForNewOption,
    currentEntityData,
    entityType,
    fieldsResponse?.fields,
    updateEntityFieldAsync,
    onUpdateFieldWithAdditionals
  ]);

  // A fresh object each render invalidates sectionsWithFields below, which
  // rebuilds every field config and its popOverFooter closures, and DetailFields
  // then re-renders off those.
  const context: DetailFieldsContext = useMemo(
    () => ({
      showCreateOptionModal,
      newOptionName,
      currentFieldForNewOption,
      setShowCreateOptionModal,
      setNewOptionName,
      setCurrentFieldForNewOption,
      handleCreateNewOption,
      onUpdateField: onUpdateFieldWithAdditionals
    }),
    [
      showCreateOptionModal,
      newOptionName,
      currentFieldForNewOption,
      handleCreateNewOption,
      onUpdateFieldWithAdditionals
    ]
  );

  const isNewFieldsUI = useNewFieldsUI();

  const sectionsWithFields: SectionWithFields[] = useMemo(() => {
    if (!fieldsResponse?.fields || !fieldsSectionsResponse?.fieldsSections) {
      return [];
    }

    const additionalFields = fieldsResponse.fields.filter(
      (field) => field.section
    );

    const fieldsBySection = groupBy(additionalFields, "section");

    const sections = getPageFieldSections(
      fieldsSectionsResponse.fieldsSections,
      FieldSectionPage.ADDITIONAL
    )
      .map((section) => {
        const sectionFields = fieldsBySection[section.id] || [];
        if (sectionFields.length === 0) return null;

        const sortedFields = sortBy(sectionFields, "order");

        const fieldsConfig = createFieldsConfigFromAPI(
          sortedFields,
          currentEntityData,
          entityType,
          t,
          thresholds,
          context,
          isNewFieldsUI
        );

        return {
          section,
          fields: sortedFields,
          fieldsConfig
        };
      })
      .filter((item): item is SectionWithFields => item !== null);

    return sortBy(sections, (s) => s.section.order);
  }, [
    fieldsResponse?.fields,
    fieldsSectionsResponse?.fieldsSections,
    currentEntityData,
    entityType,
    t,
    thresholds,
    context,
    isNewFieldsUI
  ]);

  if (isLoading) {
    return (
      <div className="flex w-full h-full items-center justify-center p-6">
        <InlineLoader label={t("loading")} />
      </div>
    );
  }

  const leftColumnSections = sectionsWithFields.filter(
    (_, index) => index % 2 === 0
  );
  const rightColumnSections = sectionsWithFields.filter(
    (_, index) => index % 2 === 1
  );

  const renderSection = ({ section, fieldsConfig }: SectionWithFields) => (
    <div
      key={section.id}
      className="bg-surface-elevation1 border-[0.5px] border-border-border1 w-full rounded-xl p-6"
    >
      <Text variant="lgSubtitleH2" className="mb-4">
        {section.name}
      </Text>
      <DetailFields
        entity={currentEntityData}
        fieldsConfig={fieldsConfig}
        onUpdateField={onUpdateFieldWithAdditionals}
        tPrefix="additionalInfo"
        context={context}
        hideContainer
      />
    </div>
  );

  return (
    <div className="flex gap-6">
      {sectionsWithFields?.length === 1 ? (
        sectionsWithFields.map(renderSection)
      ) : (
        <>
          <div className="flex flex-col gap-6 flex-1">
            {leftColumnSections.map(renderSection)}
          </div>
          <div className="flex flex-col gap-6 flex-1">
            {rightColumnSections.map(renderSection)}
          </div>
        </>
      )}
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => {
          setShowCreateOptionModal(false);
          setNewOptionName("");
          setCurrentFieldForNewOption(null);
        }}
        onCreateOption={context.handleCreateNewOption || (async () => {})}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
    </div>
  );
};

export default AdditionalInfoTab;
